import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import {
  ReportContentData,
  ReportContentPayload,
  ReportReason,
  ReportSeverityOptions,
} from '../../interfaces/report-content';
import { ReportContentService } from '../../services/reports/report-content.service';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-report-content-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ClickOutsideDirective],
  templateUrl: './report-content-modal.component.html',
  styleUrl: './report-content-modal.component.scss',
})
export class ReportContentModalComponent extends DestroyBase implements OnInit {
  private reportContentService = inject(ReportContentService);

  @ViewChild('modalBody') modalBody?: ElementRef<HTMLDivElement>;

  @Input() isOpen = false;

  reportContent: ReportContentData | null = null;
  reportReasons: ReportReason[] = [];

  public selectedReason: ReportReason | null = null;
  public selectedSeverity = 1;
  public readonly severityOptions = ReportSeverityOptions;
  public details = '';

  // File Upload properties
  public attachedFiles: File[] = [];
  public fileError: string | null = null;
  public isDragging = false;
  public isSubmitting = false;
  public submitErrorMessage: string | null = null;

  public readonly maxFiles = 3;
  public readonly maxFileSizeMB = 5;
  public readonly maxFileSizeBytes = 5 * 1024 * 1024;
  public readonly acceptedFileTypes = 'image/*,.pdf,application/pdf';
  private readonly allowedExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.bmp',
    '.svg',
    '.pdf',
  ];

  ngOnInit(): void {
    this.reportContent = this.reportContentService.getReportContent();

    this.reportContentService.modalData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.reportContent = res;
      });
    this.reportContentService.reasons$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.reportReasons = res;
      });
    this.reportContentService.isSubmitting$
      .pipe(takeUntil(this.destroy$))
      .subscribe((submitting) => {
        this.isSubmitting = submitting;
      });
    this.reportContentService.errorMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.submitErrorMessage = msg;
        if (msg) {
          this.scrollToBottom();
        }
      });
    this.reportContentService.isOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        if (!isOpen) {
          this.resetForm();
        }
      });
  }

  public scrollToBottom(): void {
    setTimeout(() => {
      if (this.modalBody?.nativeElement) {
        this.modalBody.nativeElement.scrollTo({
          top: this.modalBody.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 50);
  }

  public clearSubmitError(): void {
    if (this.submitErrorMessage) {
      this.submitErrorMessage = null;
      this.reportContentService.clearError();
    }
  }

  private getTimestamp(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  }

  private appendTimestamp(
    fileName: string,
    existingNames: string[] = [],
  ): string {
    const timestamp = this.getTimestamp();
    const lastDotIndex = fileName.lastIndexOf('.');
    let baseName = fileName;
    let ext = '';
    if (lastDotIndex > 0) {
      baseName = fileName.substring(0, lastDotIndex);
      ext = fileName.substring(lastDotIndex);
    }

    let candidateName = `${baseName}_${timestamp}${ext}`;
    let counter = 1;
    while (existingNames.includes(candidateName)) {
      candidateName = `${baseName}_${timestamp}_${counter}${ext}`;
      counter++;
    }
    return candidateName;
  }

  private isAllowedFileType(file: File): boolean {
    if (file.type) {
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        return true;
      }
    }
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    return this.allowedExtensions.includes(ext);
  }

  public addFiles(files: FileList | File[]): void {
    if (this.isSubmitting) return;

    this.fileError = null;
    this.clearSubmitError();
    const fileArray = Array.from(files);

    if (this.attachedFiles.length + fileArray.length > this.maxFiles) {
      this.fileError = `Maximum ${this.maxFiles} files allowed.`;
      this.scrollToBottom();
      return;
    }

    for (const file of fileArray) {
      if (!this.isAllowedFileType(file)) {
        this.fileError = `Only image files (PNG, JPG, GIF, WEBP) and PDF documents are allowed.`;
        this.scrollToBottom();
        return;
      }
      if (file.size > this.maxFileSizeBytes) {
        this.fileError = `"${file.name}" exceeds the ${this.maxFileSizeMB}MB limit.`;
        this.scrollToBottom();
        return;
      }
    }

    const existingNames = this.attachedFiles.map((f) => f.name);
    const renamedFiles: File[] = [];

    for (const file of fileArray) {
      const timestampedName = this.appendTimestamp(file.name, existingNames);
      existingNames.push(timestampedName);
      const renamedFile = new File([file], timestampedName, {
        type: file.type,
        lastModified: file.lastModified,
      });
      renamedFiles.push(renamedFile);
    }

    this.attachedFiles = [...this.attachedFiles, ...renamedFiles];
    this.scrollToBottom();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.addFiles(input.files);
      input.value = '';
    }
  }

  public onDragOver(event: DragEvent): void {
    if (this.isSubmitting) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    if (this.isSubmitting) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    if (this.isSubmitting) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  public removeFile(index: number): void {
    if (this.isSubmitting) return;
    this.attachedFiles.splice(index, 1);
    this.fileError = null;
    this.clearSubmitError();
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  public onClose(): void {
    if (this.isSubmitting) {
      return;
    }
    this.resetForm();
    this.reportContentService.close();
  }

  public onSubmit(): void {
    if (!this.selectedReason || this.isSubmitting) {
      return;
    }
    this.clearSubmitError();

    const payload: ReportContentPayload = {
      reason_id: this.selectedReason.id,
      severity: this.selectedSeverity,
      details: this.details?.trim(),
      file_ids: this.attachedFiles.length > 0 ? this.attachedFiles : undefined,
    };
    this.reportContentService.createReport(payload);
  }

  private resetForm(): void {
    this.selectedReason = null;
    this.selectedSeverity = 1;
    this.details = '';
    this.attachedFiles = [];
    this.fileError = null;
    this.submitErrorMessage = null;
  }

  selectReason(reason: ReportReason): void {
    if (this.isSubmitting) return;
    this.selectedReason = reason;
    this.clearSubmitError();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeHandler(event: Event): void {
    if (this.isOpen && !this.isSubmitting) {
      event.preventDefault();
      this.onClose();
    }
  }
}
